import { renderPageShell } from '../lib/page-shell';
import { fetchCurrencyPacks, type CurrencyPack } from '../lib/catalog';
import { authorizedFetch, fetchMe } from '../lib/session';
import type { RouteHandler } from '../lib/router';

// Buys Ticks or Tocks with real money. Same TEST MODE checkout/webhook
// shape as the legacy cosmetic store (`store.ts`) — no real Stripe account,
// card details or money are ever involved unless the owner sets Stripe
// secrets. This is the only way to acquire Tocks beyond the two narrow
// exceptions described on the Inventory screen (a starting grant, a few
// flagship achievements).

interface PackCheckoutResponse {
  sessionId: string;
  pack: CurrencyPack;
  testMode: boolean;
  testCard?: string;
  checkoutUrl?: string;
}

export const renderCurrencyStore: RouteHandler = (container, navigate, query) => {
  const body = renderPageShell(container, navigate, 'account', {
    title: 'GET CURRENCY',
    subtitle: 'TEST MODE. No real account, no real card, no real money. Ticks can also be earned by playing; Tocks cannot.',
    body: `<div id="currency-store-mount"><p class="page-body__stub">Loading…</p></div>`,
  });

  const mount = body.querySelector<HTMLDivElement>('#currency-store-mount')!;

  async function renderCatalogue() {
    const me = await fetchMe();
    if (!me) {
      mount.innerHTML = `
        <div class="handoff">
          <p class="handoff__label">Sign in required</p>
          <h2>Buying currency is tied to your account</h2>
          <button class="button button--primary" type="button" data-action="account">Go to account</button>
        </div>
      `;
      mount.querySelector<HTMLButtonElement>('[data-action="account"]')?.addEventListener('click', () => navigate('account'));
      return;
    }

    const { packs } = await fetchCurrencyPacks();
    const bySection: Record<string, CurrencyPack[]> = {};
    for (const pack of packs) {
      (bySection[pack.currencyName] ??= []).push(pack);
    }

    mount.innerHTML = Object.entries(bySection)
      .map(
        ([currencyName, group]) => `
          <h2 class="history-heading">${currencyName}</h2>
          <div class="cosmetics-grid">
            ${group
              .map(
                (pack) => `
                  <div class="choice-card cosmetic-card">
                    <strong>${pack.name}</strong>
                    <small>$${(pack.priceCents / 100).toFixed(2)}</small>
                    <button class="button button--secondary" type="button" data-action="buy" data-pack-id="${pack.id}">Buy</button>
                  </div>
                `,
              )
              .join('')}
          </div>
        `,
      )
      .join('');

    mount.querySelectorAll<HTMLButtonElement>('[data-action="buy"]').forEach((button) => {
      button.addEventListener('click', () => startCheckout(button.dataset.packId!));
    });
  }

  async function startCheckout(packId: string) {
    mount.innerHTML = `<div class="handoff"><p class="handoff__label">Starting checkout…</p></div>`;
    try {
      const checkout = await authorizedFetch<PackCheckoutResponse>('/api/store/checkout', {
        method: 'POST',
        body: JSON.stringify({ packId }),
      });
      if (checkout.testMode) {
        renderTestCheckout(checkout);
      } else if (checkout.checkoutUrl) {
        window.location.href = checkout.checkoutUrl;
      } else {
        renderError('Checkout did not return a payment link.');
      }
    } catch (error) {
      renderError(error instanceof Error ? error.message : 'Could not start checkout.');
    }
  }

  function renderTestCheckout(checkout: PackCheckoutResponse) {
    mount.innerHTML = `
      <div class="handoff">
        <p class="handoff__label">TEST MODE CHECKOUT. No real payment</p>
        <h2>${checkout.pack.name}: $${(checkout.pack.priceCents / 100).toFixed(2)}</h2>
        <p class="round-stage__hint">Simulated card: <strong>${checkout.testCard}</strong> (Stripe's public test number, no funds ever move).</p>
        <div class="hero-actions">
          <button class="button button--primary" type="button" data-action="confirm">Confirm test payment</button>
          <button class="button button--secondary" type="button" data-action="cancel">Cancel</button>
        </div>
      </div>
    `;
    mount.querySelector<HTMLButtonElement>('[data-action="confirm"]')?.addEventListener('click', async (event) => {
      (event.target as HTMLButtonElement).disabled = true;
      try {
        await authorizedFetch('/api/store/webhook', { method: 'POST', body: JSON.stringify({ sessionId: checkout.sessionId }) });
        renderSuccess(checkout.pack);
      } catch (error) {
        renderError(error instanceof Error ? error.message : 'Could not complete the test purchase.');
      }
    });
    mount.querySelector<HTMLButtonElement>('[data-action="cancel"]')?.addEventListener('click', renderCatalogue);
  }

  function renderSuccess(pack?: CurrencyPack) {
    mount.innerHTML = `
      <div class="handoff">
        <p class="handoff__label">Purchase complete</p>
        <h2>${pack ? `${pack.name} credited` : 'Currency credited'}</h2>
        <div class="hero-actions">
          <button class="button button--primary" type="button" data-action="inventory">View inventory</button>
          <button class="button button--secondary" type="button" data-action="store">Buy more</button>
        </div>
      </div>
    `;
    mount.querySelector<HTMLButtonElement>('[data-action="inventory"]')?.addEventListener('click', () => navigate('inventory'));
    mount.querySelector<HTMLButtonElement>('[data-action="store"]')?.addEventListener('click', renderCatalogue);
  }

  function renderError(message: string) {
    mount.innerHTML = `
      <div class="handoff">
        <p class="handoff__label">Error</p>
        <h2>${message}</h2>
        <button class="button button--secondary" type="button" data-action="back">Back</button>
      </div>
    `;
    mount.querySelector<HTMLButtonElement>('[data-action="back"]')?.addEventListener('click', renderCatalogue);
  }

  const purchaseStatus = query.get('purchase');
  if (purchaseStatus === 'success') {
    renderSuccess();
  } else if (purchaseStatus === 'cancelled') {
    mount.innerHTML = `<div class="handoff"><p class="handoff__label">Checkout cancelled</p></div>`;
    setTimeout(renderCatalogue, 1200);
  } else {
    renderCatalogue();
  }
};
